/**
 * Canonical JSON: the single byte-exact serialization used for hashing and
 * signing. Two honest implementations must always produce identical bytes,
 * otherwise signatures and hashes would not verify.
 *
 * Rules:
 *  - object keys sorted by UTF-16 code unit order, no whitespace
 *  - numbers must be safe integers (amounts are always decimal strings)
 *  - undefined, functions, bigint, NaN, Infinity, floats are rejected
 */
export type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

export function canonicalize(value: unknown): string {
  return enc(value, '$');
}

function enc(v: unknown, path: string): string {
  if (v === null) return 'null';
  switch (typeof v) {
    case 'boolean':
      return v ? 'true' : 'false';
    case 'number':
      if (!Number.isSafeInteger(v)) throw new Error(`canonical: non-integer number at ${path}`);
      return Object.is(v, -0) ? '0' : String(v);
    case 'string':
      return JSON.stringify(v);
    case 'object': {
      if (Array.isArray(v)) return '[' + v.map((x, i) => enc(x, `${path}[${i}]`)).join(',') + ']';
      const proto = Object.getPrototypeOf(v);
      if (proto !== Object.prototype && proto !== null) throw new Error(`canonical: non-plain object at ${path}`);
      const keys = Object.keys(v as object).sort();
      const parts: string[] = [];
      for (const k of keys) {
        const x = (v as Record<string, unknown>)[k];
        if (x === undefined) throw new Error(`canonical: undefined at ${path}.${k}`);
        parts.push(JSON.stringify(k) + ':' + enc(x, `${path}.${k}`));
      }
      return '{' + parts.join(',') + '}';
    }
    default:
      throw new Error(`canonical: unsupported ${typeof v} at ${path}`);
  }
}
